'use client';

import { useCallback, useState } from 'react';
import { Ticket } from 'lucide-react';
import { cn } from '@/components/atoms/primitives/utils';
import { trackExternalLinkClick } from '@/lib/analytics';
import { buildAffiliateUrl, formatTicketPrice, isUsableHttpUrl } from '@/lib/ticketing';
import { TicketHandoffDialog } from './TicketHandoffDialog';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type StickyCtaAvailability = 'available' | 'selling-fast' | 'few-left' | 'sold-out';

export interface StickyBottomCTABarViewProps {
  eventId: string;
  ticketHref: string | null;
  priceLabel: string | null;
  providerName: string | null;
  availability: StickyCtaAvailability;
  isFreeEvent: boolean;
  onCtaClick: () => void;
  className?: string;
}

export interface StickyBottomCTABarProps {
  eventId: string;
  eventStatus: string;
  ticketUrl: string | null;
  price: string | null;
  currency: string | null;
  priceRange: string | null;
  ticketAvailability: StickyCtaAvailability | null;
  providerName: string | null;
  /** Venue name — used in the Ticket Handoff bridge modal subline (FRE-724). */
  venueName?: string | null;
  isFreeEvent?: boolean;
  className?: string;
}

// ---------------------------------------------------------------------------
// View
// ---------------------------------------------------------------------------

const URGENCY_BADGE_CLASS =
  'bg-clt-skyline-glow/15 text-clt-skyline-glow border-clt-skyline-glow/30';

const AVAILABILITY_BADGES: Record<
  Exclude<StickyCtaAvailability, 'available' | 'sold-out'>,
  { label: string; badgeClass: string }
> = {
  'selling-fast': {
    label: 'Selling Fast',
    badgeClass: URGENCY_BADGE_CLASS,
  },
  'few-left': {
    label: 'Few Left',
    badgeClass: URGENCY_BADGE_CLASS,
  },
};

export function StickyBottomCTABarView({
  eventId,
  ticketHref,
  priceLabel,
  providerName,
  availability,
  isFreeEvent,
  onCtaClick,
  className,
}: StickyBottomCTABarViewProps) {
  const isSoldOut = availability === 'sold-out';
  const urgencyBadge =
    availability === 'selling-fast' || availability === 'few-left'
      ? AVAILABILITY_BADGES[availability]
      : null;

  // Nothing to sell and no free-event signal — render nothing.
  if (!isFreeEvent && !isSoldOut && !ticketHref) {
    return null;
  }

  return (
    <div
      data-testid="sticky-bottom-cta-bar"
      data-event-id={eventId}
      role="region"
      aria-label="Buy tickets"
      className={cn(
        'fixed inset-x-0 bottom-0 z-40 min-[981px]:hidden',
        'border-t border-border bg-background/95 backdrop-blur',
        'px-4 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))]',
        'shadow-[0_-4px_16px_rgba(0,0,0,0.08)]',
        className,
      )}
    >
      <div className="mx-auto flex max-w-lg items-center gap-3">
        <div className="flex min-w-0 flex-1 flex-col">
          {urgencyBadge && (
            <span
              data-testid="sticky-urgency-badge"
              className={cn(
                'mb-1 inline-flex w-fit items-center rounded-full border px-2 py-0.5 text-micro font-semibold uppercase tracking-wide',
                urgencyBadge.badgeClass,
              )}
            >
              {urgencyBadge.label}
            </span>
          )}
          {priceLabel ? (
            <>
              <span className="font-mono text-micro uppercase tracking-wide text-muted-foreground">
                {/* "From" reads naturally only for ranges (e.g. "$25 – $120").
                    Single prices and "Free" use "Price" to avoid the awkward
                    "From $45" copy flagged in review. */}
                {!isFreeEvent && priceLabel.includes('–') ? 'From' : 'Price'}
              </span>
              <span
                data-testid="sticky-price"
                className="font-display text-lg font-bold leading-tight text-foreground"
              >
                {priceLabel}
              </span>
            </>
          ) : (
            <span data-testid="sticky-price-fallback" className="text-xs text-muted-foreground">
              See pricing on {providerName ?? 'provider'}
            </span>
          )}
        </div>

        {isSoldOut ? (
          <button
            type="button"
            disabled
            data-testid="sticky-cta-sold-out"
            aria-label="Sold Out"
            className={cn(
              'inline-flex min-h-[44px] desktop:min-h-0 shrink-0 items-center justify-center gap-2 rounded-lg px-5 py-3 text-sm font-semibold',
              'bg-muted text-muted-foreground opacity-60 pointer-events-none',
            )}
          >
            <Ticket size={16} aria-hidden="true" />
            Sold Out
          </button>
        ) : ticketHref ? (
          <button
            type="button"
            onClick={onCtaClick}
            data-testid="sticky-cta-button"
            aria-label="Buy Tickets. Review partner details before handoff"
            aria-haspopup="dialog"
            className={cn(
              'inline-flex min-h-[44px] desktop:min-h-0 shrink-0 items-center justify-center gap-2 rounded-lg px-[18px] py-4 font-display text-sm font-bold',
              'bg-clt-ember text-primary-foreground transition-colors hover:bg-clt-ember-light active:bg-clt-ember',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-clt-ember focus-visible:ring-offset-2',
            )}
          >
            <Ticket size={16} aria-hidden="true" />
            Buy Tickets
          </button>
        ) : null}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Connected
// ---------------------------------------------------------------------------

// Parse the first decimal number out of a free-form price string for the
// Ticket Handoff modal's final-price disclosure. Returns null when no usable
// number can be extracted (ranges, "Free", missing).
function parsePriceAsNumber(price: string | null): number | null {
  if (!price) return null;
  // Ranges ("$25 – $120", "25.00 - 120.00") cannot be shown as a single
  // final-price figure; return null so the price block is omitted.
  if (/–|-/.test(price)) return null;
  const match = price.match(/\d+(?:\.\d+)?/);
  if (!match) return null;
  const parsed = Number.parseFloat(match[0]);
  return Number.isFinite(parsed) ? parsed : null;
}

export function StickyBottomCTABar({
  eventId,
  eventStatus,
  ticketUrl,
  price,
  currency,
  priceRange,
  ticketAvailability,
  providerName,
  venueName = null,
  isFreeEvent = false,
  className,
}: StickyBottomCTABarProps) {
  const [handoffOpen, setHandoffOpen] = useState(false);
  const normalizedTicketUrl = isUsableHttpUrl(ticketUrl) ? ticketUrl : null;
  const affiliateUrl = !isFreeEvent && eventId ? buildAffiliateUrl(eventId) : null;
  const ticketHref = affiliateUrl ?? normalizedTicketUrl;

  const handleCtaClick = useCallback(() => {
    if (!ticketHref) return;
    trackExternalLinkClick(eventId, ticketHref, 'tickets');
    setHandoffOpen(true);
  }, [eventId, ticketHref]);

  // Cancelled events never expose a purchase path — bail after hooks so the
  // hook order stays stable across renders (react-hooks/rules-of-hooks).
  if (eventStatus === 'CANCELLED') {
    return null;
  }

  const availability: StickyCtaAvailability =
    eventStatus === 'SOLD_OUT' ? 'sold-out' : (ticketAvailability ?? 'available');

  const priceLabel = isFreeEvent ? 'Free' : (priceRange ?? formatTicketPrice(price, currency));
  const partnerName = providerName ?? 'our ticket partner';
  const finalPrice = parsePriceAsNumber(price);

  return (
    <>
      <StickyBottomCTABarView
        eventId={eventId}
        ticketHref={ticketHref}
        priceLabel={priceLabel}
        providerName={providerName}
        availability={availability}
        isFreeEvent={isFreeEvent}
        onCtaClick={handleCtaClick}
        className={className}
      />
      {ticketHref && (
        <TicketHandoffDialog
          open={handoffOpen}
          onOpenChange={setHandoffOpen}
          eventId={eventId}
          partnerName={partnerName}
          venueName={venueName}
          finalPrice={finalPrice}
          currency={currency ?? 'USD'}
          affiliateUrl={ticketHref}
        />
      )}
    </>
  );
}
